import { api } from './api';
import { DeeplinkResolution, Notification, NotificationResourceType } from '@/types/notification';

export interface ResolveParams {
  project_id?: number | null;
  resource_type?: NotificationResourceType | null;
  resource_id?: number | null;
}

export interface NotificationListOptions {
  page?: number;
  limit?: number;
}

/**
 * Raw backend response that may contain both Notification-compatible and
 * legacy AlertLog fields. We use a loose intersection so TypeScript
 * accepts the object from the API and allows accessing legacy keys.
 */
interface RawNotification extends Notification {
  recipient_id?: number;
  item_type?: string;
  item_id?: number;
  subject?: string;
  sent_at?: string;
  read_at?: string | null;
}

/**
 * Normalizes a raw backend response into a Notification object.
 * Handles both new mapped format and legacy AlertLog format as safety-net.
 */
function normalizeNotification(raw: RawNotification): Notification {
  return {
    id: raw.id,
    user_id: raw.user_id ?? raw.recipient_id ?? 0,
    project_id: raw.project_id ?? null,
    resource_type: raw.resource_type ?? (raw.item_type as Notification['resource_type']) ?? 'project',
    resource_id: raw.resource_id ?? raw.item_id ?? null,
    message: raw.message ?? raw.subject ?? 'Notificação',
    deeplink_path: raw.deeplink_path ?? null,
    is_obsolete: raw.is_obsolete ?? false,
    is_read: typeof raw.is_read === 'boolean' ? raw.is_read : raw.read_at != null,
    created_at: raw.created_at ?? raw.sent_at ?? new Date().toISOString(),
    updated_at: raw.updated_at ?? raw.sent_at ?? new Date().toISOString(),
    deleted_at: raw.deleted_at ?? null,
    alert_type: raw.alert_type ?? null,
  };
}

export const notificationService = {
  async getNotifications(options: NotificationListOptions = {}): Promise<Notification[]> {
    const { page = 1, limit = 50 } = options;
    const result = await api.get<{ unread_count: number; notifications: RawNotification[] }>('/alerts/notifications', {
      page: String(page),
      limit: String(limit),
    });
    return (result.notifications || []).map(normalizeNotification);
  },

  async markAsRead(notificationId: number): Promise<{ message: string }> {
    const result = await api.put<{ message: string }>(`/alerts/notifications/${notificationId}/read`, {});
    return result;
  },

  async markAllAsRead(): Promise<{ message: string }> {
    const result = await api.put<{ message: string }>('/alerts/notifications/mark-all-read', {});
    return result;
  },

  async dismissRead(): Promise<{ message: string; dismissed_count: number }> {
    return api.delete<{ message: string; dismissed_count: number }>('/alerts/notifications/dismiss-read');
  },

  async resolveDeeplink(params: ResolveParams): Promise<DeeplinkResolution> {
    try {
      const result = await api.post<DeeplinkResolution>('/notifications/resolve-deeplink', params);
      return result;
    } catch (error) {
      console.error('[notificationService] Deeplink resolution failed:', error);
      throw error;
    }
  },
};
